import {
  useGetEnrollmentMultiDocumentVerificationCompleteData,
  useSubmitEnrollmentMultiDocumentVerificationCompleteData,
} from '@egw/api'
import { Alert, AlertType, Button, GenericError } from '@egw/components'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { EnrollmentMultiLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { WEALTH_ROUTES } from '@egw/constants'
import { PageHeader } from '../../../components'
import { ALL_ENROLLMENT_ROUTES } from '../../../constants'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'

const log = new Logger('Enrollment - Multi - DocumentVerificationComplete')

export const DocumentVerificationCompletePage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  const { data, isLoading, error } = useGetEnrollmentMultiDocumentVerificationCompleteData()
  const {
    mutate,
    isLoading: isSubmitting,
    error: submitError,
  } = useSubmitEnrollmentMultiDocumentVerificationCompleteData()

  // Protect route
  useValidateRoute(data?.path)

  const onSubmit = async (): Promise<void> => {
    try {
      sendEvent('bankmulti_enroll_docV_ack_submit')

      const response = await mutate({
        proceedDocumentVerificationComplete: true,
      })
      // If path exists and is different
      if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
        void navigate(response.path)
      } else if (!response?.path) {
        void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }
  // AUTO GENERATED FORM HANDLERS END

  return (
    <EnrollmentMultiLayout title={t('multi.verification-complete.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <>
          <CheckCircleIcon className="h-[4rem] w-[4rem] stroke-textSuccess stroke-[.5px] mb-sm" />

          <PageHeader className="mb-md text-text-header-success" title={t('multi.verification-complete.title')} />
          <p className="typo-primary-base-regular text-text-meta">{t('multi.verification-complete.description')}</p>

          <div className="flex-1 md:hidden" />

          {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

          {/* AUTO GENERATED COMPONENTS */}

          <Button
            type="submit"
            className="w-full md:w-min mt-xl"
            isLoading={isSubmitting}
            testId="primaryAction_button"
            onClick={() => void onSubmit()}
          >
            {t('common.continue')}
          </Button>
        </>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentMultiLayout>
  )
}
